<script setup lang="ts" generic="Item extends ItemBase, T extends Constructable">
import { type App, computed, createApp, h, onUnmounted, ref } from "vue";
import { DynamicScrollerItem } from "vue-virtual-scroller";

import type { Constructable } from "~/shared/typeUtils";
import { useAppStore, useDataStore } from "~/stores";

import DragGhost from "./DragGhost.vue";
import { removeDragGhost, setDragGhost } from "./dragGhost";
import useDragStore from "./DragStore";
import { type DragPointer } from "./dragScroll";
import IndentationLine from "./IndentationLine.vue";
import { type ItemBase } from "./shared";

const TOUCH_DRAG_START_DISTANCE_PX = 10;

const props = defineProps<{
  group: string;
  category: string;
  index: number;
  level: number;
  currentItem: Item;
  allItems: Item[];
  component: T;
  getComponentProps: (item: Item, index: number) => InstanceType<T>["$props"];
  placeholderColor: string;
  disabled: boolean;
  gap: number;
  subitemGap: number;
  itemSidePadding: number;
  indentationPx: number;
  indentationLine: boolean;
  activeInScroll: boolean;
  dragHandle: string | undefined;
}>();

const emit = defineEmits<{
  dragStart: [event: DragEvent];
  dragEnd: [event: DragEvent];
  dragEnter: [event: DragEvent];
  dragOver: [event: DragEvent];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const dragStore = useDragStore();

const wrapper = ref<HTMLDivElement | null>(null);
const itemRef = ref<InstanceType<T> | null>(null);

const draggingItems = computed(() => dragStore.getDraggingItemsForGroup<Item>(props.group));
const descendantDraggingItems = computed(() => dragStore.getDraggingDescendantItemsForGroup<Item>(props.group));
const isDraggingGroup = computed(() => draggingItems.value.concat(descendantDraggingItems.value).length > 0);
const isDragged = computed(
  () =>
    draggingItems.value.some((e) => e.duid === props.currentItem.duid) ||
    descendantDraggingItems.value.some((e) => e.duid === props.currentItem.duid)
);
const isSubitem = computed(() => !!props.currentItem.parentDuid);

const createMultiTaskGhostElement = (count: number) => {
  const app = createApp({
    render: () => h(DragGhost, { count }),
  });

  const ghost = app.mount(document.createElement("div"));
  return { element: ghost.$el as HTMLDivElement, app };
};

const dragStartElement = ref<HTMLDivElement | null>(null);
const initialTouchPointer = ref<DragPointer | null>(null);
const onWindowDragOver = (e: DragEvent) => {
  const pointer = initialTouchPointer.value;
  if (!pointer || Math.hypot(e.clientX - pointer.x, e.clientY - pointer.y) <= TOUCH_DRAG_START_DISTANCE_PX) {
    return;
  }

  window.removeEventListener("dragover", onWindowDragOver);
  emit("dragStart", e);
};

const onDragStart = async (e: DragEvent) => {
  if (!e.dataTransfer || !itemRef.value?.$el || e.target !== wrapper.value) {
    return;
  }

  // If dragHandle is set, only allow dragging from elements with that class
  if (props.dragHandle) {
    const clickedElement = dragStartElement.value;
    const handleElement = clickedElement?.closest(`.${props.dragHandle}`);
    if (!handleElement || (wrapper.value && !wrapper.value.contains(handleElement))) {
      e.preventDefault();
      return;
    }
  }

  const element: HTMLDivElement = itemRef.value.$el;
  const selectedItems = appStore.selectedTaskDuids.size;

  let ghost: HTMLDivElement;
  let ghostApp: App | null = null;

  if (selectedItems > 1 && appStore.selectedTaskDuids.has(props.currentItem.duid)) {
    const multiTaskGhost = createMultiTaskGhostElement(selectedItems);
    ghost = multiTaskGhost.element;
    ghostApp = multiTaskGhost.app;
  } else {
    ghost = element.cloneNode(true) as HTMLDivElement;
    ghost.style.maxWidth = `${element.offsetWidth}px`;
    ghost.style.maxHeight = `${element.offsetHeight}px`;
    ghost.style.backgroundColor = props.placeholderColor;
    ghost.classList.add("rounded");
  }
  document.body.appendChild(ghost);
  setDragGhost(ghost, ghostApp);
  e.dataTransfer.setDragImage(ghost, 10, 1);

  // oxlint-disable-next-line no-param-reassign
  e.dataTransfer.effectAllowed = "move";

  const draggedItems =
    appStore.selectedTaskDuids.has(props.currentItem.duid) && appStore.selectedTaskDuids.size > 1
      ? dataStore
          .getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
            includeTrashed: false,
            includeDraft: false,
          })
          .map((task) => task)
      : [props.currentItem];

  if (selectedItems > 1 && appStore.selectedTaskDuids.has(props.currentItem.duid)) {
    e.dataTransfer.setData("text/plain", `${selectedItems} tasks`);
    e.dataTransfer.setData("application/json", JSON.stringify(draggedItems));
  } else {
    e.dataTransfer.setData("text/plain", props.currentItem.title);
    e.dataTransfer.setData("application/json", JSON.stringify(draggedItems));
  }
  if (initialTouchPointer.value) {
    window.addEventListener("dragover", onWindowDragOver);
    return;
  }

  emit("dragStart", e);

  // Set cursor, chrome is buggy and needs a timeout https://github.com/SortableJS/Vue.Draggable/issues/815#issuecomment-1552904628
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => {
    document.body.classList.add("dragging-item");
  }, 50);
};

const onDragEnd = (e: DragEvent) => {
  e.preventDefault();
  if (e.dataTransfer) {
    e.dataTransfer.clearData();
  }
  window.removeEventListener("dragover", onWindowDragOver);
  removeDragGhost();
  document.body.classList.remove("dragging-item");
  document.body.style.cursor = "";

  emit("dragEnd", e);
};

const onDragEnter = (e: DragEvent) => {
  e.preventDefault();
  if (wrapper.value?.classList.contains("drag-list-move")) {
    return;
  }

  emit("dragEnter", e);
};

const onDragOver = (e: DragEvent) => {
  e.preventDefault();
  if (!isDragged.value || wrapper.value?.classList.contains("drag-list-move")) {
    return;
  }
  emit("dragOver", e);
};

const onPointerDown = (e: PointerEvent) => {
  if (!e.target) {
    return;
  }
  dragStartElement.value = e.target as HTMLDivElement;
  initialTouchPointer.value = e.pointerType === "touch" ? { x: e.clientX, y: e.clientY } : null;
};
const onPointerUp = () => {
  dragStartElement.value = null;
};

const setRef = (elem?: { $el: HTMLDivElement }) => {
  wrapper.value = elem?.$el ?? null;
};

onUnmounted(() => {
  removeDragGhost();
});

defineExpose({
  wrapper,
  itemRef,
});
</script>

<template>
  <DynamicScrollerItem
    :ref="setRef"
    :item="currentItem"
    :active="activeInScroll"
    class="w-full"
    :draggable="!disabled"
    :data-index="index"
    @pointerdown="onPointerDown"
    @pointerup="onPointerUp"
    @dragstart="onDragStart"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragend="onDragEnd">
    <div
      class="relative size-full"
      :style="{
        paddingLeft:
          itemSidePadding || props.level > 1
            ? `${itemSidePadding * 2 + (props.level - 1) * props.indentationPx}px`
            : undefined,
        paddingTop: props.index === 0 ? undefined : `${isSubitem ? props.subitemGap : props.gap}px`,
        paddingRight: itemSidePadding ? `${itemSidePadding}px` : undefined,
      }">
      <IndentationLine
        v-if="indentationLine && props.level > 1"
        :item-side-padding="itemSidePadding"
        :level="props.level"
        :indentation-px="props.indentationPx" />
      <component
        :is="component"
        ref="itemRef"
        class="size-full"
        :class="{ 'dart-dragging': isDragged, 'pointer-events-none': isDraggingGroup }"
        v-bind="getComponentProps(currentItem, index)" />
    </div>
  </DynamicScrollerItem>
</template>

<style scoped>
@reference "#frontend.css";
/* Item placeholder styles */
.dart-dragging {
  background-color: v-bind("placeholderColor");
  @apply rounded;
}

/* Item ghost styles */
.dart-dragging :deep(*) {
  @apply !pointer-events-none !opacity-0;
}
</style>
